import { lazy, Suspense } from 'react';
import type { ComponentType, ReactNode } from 'react';

import { PageLoader } from 'components/ui/loading/Loader';
import { importWithStaleChunkRecovery } from 'utils/chunkRecoveryUtils';

type DynamicImportResult<T extends ComponentType<any>> = {
  default: T;
};

/**
 * A higher-order component that:
 * 1) creates a JS bundle for each component instance at build time; and
 * 2) asynchronously loads the bundled component when requested at run-time
 *
 * Uses React.lazy() and Suspense for modern code splitting. Chunks belonging to a build that has
 * since been deployed over are recovered by a one-shot reload rather than the error boundary.
 */
const DynamicImport = <T extends ComponentType<any>>(
  importComponent: () => Promise<DynamicImportResult<T>>,
  fallback: ReactNode = <PageLoader />
): ComponentType<any> => {
  const LazyComponent = lazy(() => importWithStaleChunkRecovery(importComponent));

  const DynamicComponent: ComponentType<any> = props => (
    <Suspense fallback={fallback}>
      <LazyComponent {...props} />
    </Suspense>
  );

  DynamicComponent.displayName = 'DynamicImport';

  return DynamicComponent;
};

export default DynamicImport;
